/**
 * Клиентское сжатие изображения перед отправкой на бэкенд.
 *
 * Зачем это здесь. Бэкенд с картинкой не делает ничего: `ImageUploader::uploadFromPost`
 * кладёт присланные байты в объектное хранилище как есть — ни сжатия, ни ресайза, ни
 * конвертации. Единственный предел — `client_max_body_size 5M` на nginx, и превышение
 * он отдаёт HTML-страницей 413, до приложения запрос не доходит. Фото с телефона
 * (8–12 МБ) в этот предел не влезает.
 *
 * Два ограничения задают алгоритм:
 *
 * 1. **Разрешение важно.** Фото игрока показывается не только миниатюрой: карусель
 *    составов в `modules/overlay-v2` подставляет его в шаблон на канве 1920×1080, и
 *    размер плейсхолдера задаёт дизайнер шаблона. Поэтому разрешение уменьшается
 *    последним, только когда качества не хватило.
 * 2. **Прозрачность обязана сохраниться.** Вырезанные по контуру фото и логотипы
 *    кладутся поверх фона шаблона, и перевод в JPEG ломает их необратимо. При наличии
 *    альфа-канала выход — WebP (его принимает и бэкенд, и fabric).
 *
 * Файл, который и так проходит по размеру, не трогается вовсе: возвращается тот же
 * объект `File` — исходный формат, разрешение, EXIF и прозрачность на месте.
 */

import {
  CompressImageError,
  DecodedImage,
  decodeImage,
  drawToCanvas,
  hasAlphaChannel,
  ImageSize,
  renameForType,
  scaledSize,
  toBlob,
} from 'shared/utils/image/image-canvas';

export { CompressImageError };
export type { CompressImageFailureReason, ImageSize } from 'shared/utils/image/image-canvas';

/** Выше этого размера файл идёт в обработку. Запас к 5 МБ nginx — на поля формы. */
export const COMPRESS_THRESHOLD_BYTES = 4 * 1024 * 1024;

/** Бюджет результата. Совпадает с порогом: цель — уложиться, а не ужать любой ценой. */
export const TARGET_MAX_BYTES = 4 * 1024 * 1024;

/** Ниже этой стороны не опускаемся — граница приличного вида в карусели на 1080p. */
export const MIN_MAX_SIDE = 1600;

export interface CompressImageOptions {
  thresholdBytes?: number;
  targetBytes?: number;
}

export interface CompressImageResult {
  /** Готовый файл: либо новый, либо исходный без изменений. */
  file: File;
  wasCompressed: boolean;
  /** Разрешение осталось прежним. Осмысленно только при `wasCompressed`. */
  keptResolution: boolean;
  originalBytes: number;
  resultBytes: number;
  /** Размеры известны только когда файл декодировался, то есть при сжатии. */
  originalSize?: ImageSize;
  resultSize?: ImageSize;
  outputType: string;
}

/**
 * Лестница компромиссов для форматов с потерями.
 *
 * Порядок принципиален: сначала падает качество при исходном разрешении, и только
 * потом уменьшается сторона. Телефонное фото обычно укладывается на первых двух
 * ступенях — то есть в исходном разрешении.
 */
const LOSSY_STEPS: { maxSide?: number; quality: number }[] = [
  { quality: 0.92 },
  { quality: 0.85 },
  { maxSide: 2560, quality: 0.85 },
  { maxSide: 2048, quality: 0.82 },
  { maxSide: MIN_MAX_SIDE, quality: 0.8 },
];

/**
 * Лестница для PNG: качество на него не влияет, остаётся только разрешение.
 * Используется, когда есть прозрачность, а WebP браузер кодировать не умеет.
 */
const LOSSLESS_STEPS = [2560, 2048, MIN_MAX_SIDE, 1200];

/**
 * Кодирование одной ступени.
 *
 * `canvas.toBlob` по спецификации молча отдаёт PNG, если запрошенный тип не
 * поддерживается, — поэтому реальный тип берётся из самого блоба, а не из запроса.
 */
async function encodeStep(image: DecodedImage, maxSide: number | undefined, type: string, quality?: number) {
  const size = scaledSize(image.width, image.height, maxSide);
  const canvas = drawToCanvas(image, size);
  const blob = await toBlob(canvas, type, quality);

  return { blob, size, actualType: blob.type || type };
}

export async function compressImage(file: File, options: CompressImageOptions = {}): Promise<CompressImageResult> {
  const { thresholdBytes = COMPRESS_THRESHOLD_BYTES, targetBytes = TARGET_MAX_BYTES } = options;

  // Проходит по размеру — не трогаем вовсе. Перекодировать то, что и так влезает,
  // значит терять качество и прозрачность без всякой пользы.
  if (file.size <= thresholdBytes) {
    return {
      file,
      wasCompressed: false,
      keptResolution: true,
      originalBytes: file.size,
      resultBytes: file.size,
      outputType: file.type,
    };
  }

  const image = await decodeImage(file);

  try {
    const originalSize: ImageSize = { width: image.width, height: image.height };
    const needsAlpha = hasAlphaChannel(image, file.type);
    const preferredType = needsAlpha ? 'image/webp' : 'image/jpeg';

    // Первая ступень заодно показывает, умеет ли браузер кодировать WebP: если нет,
    // `toBlob` молча вернёт PNG, и дальше идти придётся лестницей разрешений.
    const [firstStep, ...restLossySteps] = LOSSY_STEPS;
    const probe = await encodeStep(image, firstStep.maxSide, preferredType, firstStep.quality);
    const outputType = probe.actualType;

    if (probe.blob.size <= targetBytes) {
      return buildResult(file, probe.blob, originalSize, probe.size, outputType);
    }

    const steps = outputType === preferredType ? restLossySteps : LOSSLESS_STEPS.map((maxSide) => ({ maxSide, quality: undefined }));

    for (const step of steps) {
      const { blob, size } = await encodeStep(image, step.maxSide, outputType, step.quality);
      if (blob.size <= targetBytes) {
        return buildResult(file, blob, originalSize, size, outputType);
      }
    }

    throw new CompressImageError('too-large', 'Не удалось уложиться в бюджет размера');
  } finally {
    image.close();
  }
}

function buildResult(
  original: File,
  blob: Blob,
  originalSize: ImageSize,
  resultSize: ImageSize,
  outputType: string,
): CompressImageResult {
  const file = new File([blob], renameForType(original.name, outputType), {
    type: outputType,
    lastModified: Date.now(),
  });

  return {
    file,
    wasCompressed: true,
    keptResolution: resultSize.width === originalSize.width && resultSize.height === originalSize.height,
    originalBytes: original.size,
    resultBytes: file.size,
    originalSize,
    resultSize,
    outputType,
  };
}
